/* ==========================================================================
   Accessibility Widget — behaviour layer (no dependencies)
   Replaces the previous third-party Enable widget.

   - Builds the toggle button + panel DOM at runtime (valid on any layout).
   - Persists every adjustment in localStorage and re-applies on each page.
   - Fully keyboard operable: Tab focus-trap, Esc to close, aria-pressed,
     aria-live announcements, focus returned to the opener on close.
   - Reads localized labels + config from the inline <script id="acc-config">.
   ========================================================================== */
(function () {
  'use strict';
  if (window.__accWidgetLoaded) return;
  window.__accWidgetLoaded = true;

  var STORAGE_KEY = 'mist-accessibility-v1';
  var root = document.documentElement;

  /* -------------------------------------------------- config + i18n ------ */
  var cfg = { accent: '#1746a2', position: 'middle-left', statement: '/pages/accessibility', lang: 'en', dir: 'ltr', t: {} };
  try {
    var node = document.getElementById('acc-config');
    if (node) {
      var parsed = JSON.parse(node.textContent || '{}');
      for (var k in parsed) { if (parsed.hasOwnProperty(k)) cfg[k] = parsed[k]; }
    }
  } catch (e) { /* fall back to defaults */ }

  // English fallbacks; the snippet supplies localized strings via cfg.t
  var T = {
    title: 'Accessibility menu', open: 'Open accessibility menu', close: 'Close',
    on: 'on', off: 'off', reset_done: 'Accessibility settings reset',
    g_profiles: 'Accessibility profiles', g_content: 'Content adjustments',
    g_color: 'Color adjustments', g_nav: 'Navigation & orientation',
    p_seizure: 'Seizure safe', p_vision: 'Vision impaired', p_adhd: 'ADHD friendly', p_keyboard: 'Keyboard navigation',
    text_size: 'Text size', decrease: 'Decrease', increase: 'Increase',
    spacing: 'Text spacing', readable_font: 'Readable font',
    highlight_links: 'Highlight links', highlight_titles: 'Highlight titles',
    align_left: 'Align left', align_center: 'Align center', align_right: 'Align right',
    invert: 'Invert colors', grayscale: 'Grayscale', contrast_dark: 'Dark contrast',
    contrast_light: 'Light contrast', sat_high: 'High saturation', sat_low: 'Low saturation',
    cursor_black: 'Big black cursor', cursor_white: 'Big white cursor',
    focus: 'Highlight focus', stop_anim: 'Stop animations', hide_images: 'Hide images',
    guide: 'Reading guide', mask: 'Reading mask', mute: 'Mute sounds',
    statement: 'Accessibility statement', reset: 'Reset all'
  };
  for (var tk in (cfg.t || {})) { if (cfg.t.hasOwnProperty(tk)) T[tk] = cfg.t[tk]; }
  function t(key) { return T[key] || key; }

  /* -------------------------------------------------- state -------------- */
  var DEFAULTS = {
    fontSize: 0, spacing: 0, readableFont: false, highlightLinks: false,
    highlightTitles: false, align: '', color: '', cursor: '', focus: false,
    stopAnim: false, hideImages: false, guide: false, mask: false, mute: false, profile: ''
  };
  var state = load();

  function load() {
    var s = {};
    for (var k in DEFAULTS) s[k] = DEFAULTS[k];
    try {
      var saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      for (var j in saved) { if (s.hasOwnProperty(j)) s[j] = saved[j]; }
    } catch (e) {}
    return s;
  }
  function save() {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (e) {}
  }

  // Base root font size captured before any scaling (set by inline bootstrap).
  var baseFontPx = parseFloat(root.getAttribute('data-acc-base') || '') ||
    parseFloat(getComputedStyle(root).fontSize) || 16;

  /* -------------------------------------------------- apply state -------- */
  function applyState() {
    var cl = root.classList;

    if (state.fontSize > 0) {
      root.style.setProperty('font-size', (baseFontPx * (1 + state.fontSize * 0.12)).toFixed(2) + 'px', 'important');
    } else {
      root.style.removeProperty('font-size');
    }

    cl.toggle('acc-spacing-1', state.spacing === 1);
    cl.toggle('acc-spacing-2', state.spacing === 2);
    cl.toggle('acc-readable-font', !!state.readableFont);
    cl.toggle('acc-highlight-links', !!state.highlightLinks);
    cl.toggle('acc-highlight-titles', !!state.highlightTitles);

    cl.toggle('acc-align-left', state.align === 'left');
    cl.toggle('acc-align-center', state.align === 'center');
    cl.toggle('acc-align-right', state.align === 'right');

    cl.toggle('acc-color-invert', state.color === 'invert');
    cl.toggle('acc-color-grayscale', state.color === 'grayscale');
    cl.toggle('acc-color-sat-high', state.color === 'sat-high');
    cl.toggle('acc-color-sat-low', state.color === 'sat-low');
    cl.toggle('acc-contrast-dark', state.color === 'contrast-dark');
    cl.toggle('acc-contrast-light', state.color === 'contrast-light');

    cl.toggle('acc-cursor-black', state.cursor === 'black');
    cl.toggle('acc-cursor-white', state.cursor === 'white');

    cl.toggle('acc-focus', !!state.focus);
    cl.toggle('acc-stop-anim', !!state.stopAnim);
    cl.toggle('acc-hide-images', !!state.hideImages);

    setGuide(!!state.guide);
    setMask(!!state.mask);
    setMute(!!state.mute);
  }

  /* -------------------------------------------------- reading guide/mask - */
  var guideEl = null, maskTop = null, maskBottom = null, lastY = 0, pointerBound = false;
  var SLIT = 110; // px height of the readable strip in mask mode

  function ensurePointer() {
    if (pointerBound) return;
    pointerBound = true;
    window.addEventListener('pointermove', onPointer, { passive: true });
    window.addEventListener('touchmove', onTouch, { passive: true });
  }
  function onPointer(e) { lastY = e.clientY; positionAids(); }
  function onTouch(e) { if (e.touches && e.touches[0]) { lastY = e.touches[0].clientY; positionAids(); } }

  function positionAids() {
    if (guideEl) guideEl.style.top = lastY + 'px';
    if (maskTop && maskBottom) {
      maskTop.style.height = Math.max(0, lastY - SLIT / 2) + 'px';
      maskBottom.style.height = Math.max(0, window.innerHeight - lastY - SLIT / 2) + 'px';
    }
  }
  function setGuide(on) {
    if (on && !guideEl) {
      guideEl = document.createElement('div');
      guideEl.className = 'acc-guide';
      guideEl.setAttribute('aria-hidden', 'true');
      document.body.appendChild(guideEl);
      ensurePointer(); lastY = lastY || window.innerHeight / 2; positionAids();
    } else if (!on && guideEl) {
      guideEl.remove(); guideEl = null;
    }
  }
  function setMask(on) {
    if (on && !maskTop) {
      maskTop = document.createElement('div'); maskTop.className = 'acc-mask-top';
      maskBottom = document.createElement('div'); maskBottom.className = 'acc-mask-bottom';
      maskTop.setAttribute('aria-hidden', 'true'); maskBottom.setAttribute('aria-hidden', 'true');
      document.body.appendChild(maskTop); document.body.appendChild(maskBottom);
      ensurePointer(); lastY = lastY || window.innerHeight / 2; positionAids();
    } else if (!on && maskTop) {
      maskTop.remove(); maskBottom.remove(); maskTop = maskBottom = null;
    }
  }

  /* -------------------------------------------------- mute --------------- */
  var muteBound = false;
  function setMute(on) {
    var media = document.querySelectorAll('video, audio');
    for (var i = 0; i < media.length; i++) media[i].muted = on;
    if (on && !muteBound) {
      muteBound = true;
      document.addEventListener('play', function (e) {
        if (state.mute && e.target && (e.target.tagName === 'VIDEO' || e.target.tagName === 'AUDIO')) {
          e.target.muted = true;
        }
      }, true);
    }
  }

  /* -------------------------------------------------- icons -------------- */
  function svg(p) {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + p + '</svg>';
  }
  var IC = {
    wheelchair: '<circle cx="12" cy="4" r="2"/><path d="M19 13v-1a1 1 0 0 0-1-1h-6V8m0 3v4a3 3 0 0 0 3 3h2l2 3"/><path d="M12 11a6 6 0 1 0 4.5 9.9"/>',
    text: '<path d="M4 7V5h16v2"/><path d="M9 5v14"/><path d="M15 5v14"/><path d="M7 19h4"/><path d="M13 19h4"/>',
    minus: '<path d="M5 12h14"/>', plus: '<path d="M12 5v14M5 12h14"/>',
    spacing: '<path d="M4 7h16M4 12h16M4 17h16"/>',
    font: '<path d="M6 20l4-12 4 12M7.5 16h5"/><path d="M16 8h4M18 8v12"/>',
    link: '<path d="M10 13a5 5 0 0 0 7 0l2-2a5 5 0 0 0-7-7l-1 1"/><path d="M14 11a5 5 0 0 0-7 0l-2 2a5 5 0 0 0 7 7l1-1"/>',
    title: '<path d="M6 4v16M18 4v16M6 12h12M3 4h6M3 20h6M15 4h6M15 20h6"/>',
    alignL: '<path d="M4 6h16M4 12h10M4 18h13"/>', alignC: '<path d="M4 6h16M7 12h10M5 18h14"/>', alignR: '<path d="M4 6h16M10 12h10M7 18h13"/>',
    invert: '<circle cx="12" cy="12" r="9"/><path d="M12 3a9 9 0 0 0 0 18z" fill="currentColor"/>',
    gray: '<circle cx="12" cy="12" r="9"/><path d="M12 3v18"/>',
    contrastD: '<circle cx="12" cy="12" r="9" fill="currentColor"/><path d="M12 3a9 9 0 0 1 0 18"/>',
    contrastL: '<circle cx="12" cy="12" r="9"/><path d="M12 7a5 5 0 0 0 0 10z"/>',
    satHi: '<circle cx="12" cy="12" r="4"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2"/>',
    satLo: '<circle cx="12" cy="12" r="4"/><path d="M12 4v2M12 18v2M4 12h2M18 12h2"/>',
    cursorB: '<path d="M5 3l14 8-6 1 3 7-3 1-3-7-5 4z" fill="currentColor"/>',
    cursorW: '<path d="M5 3l14 8-6 1 3 7-3 1-3-7-5 4z"/>',
    focus: '<rect x="3" y="3" width="18" height="18" rx="2" stroke-dasharray="3 3"/><circle cx="12" cy="12" r="3" fill="currentColor"/>',
    motion: '<circle cx="12" cy="12" r="9"/><path d="M8 8l8 8M16 8l-8 8"/>',
    image: '<rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="8.5" cy="9.5" r="1.5"/><path d="M21 16l-5-5L5 20"/>',
    guide: '<path d="M3 12h18"/><path d="M7 8l-2 4 2 4M17 8l2 4-2 4"/>',
    mask: '<rect x="3" y="3" width="18" height="6" fill="currentColor"/><rect x="3" y="15" width="18" height="6" fill="currentColor"/>',
    mute: '<path d="M5 9v6h4l5 4V5L9 9H5z"/><path d="M17 9l4 6M21 9l-4 6"/>',
    statement: '<path d="M6 2h9l5 5v15H6z"/><path d="M14 2v6h6M9 13h6M9 17h6"/>',
    reset: '<path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/>',
    seizure: '<path d="M13 2L4 14h7l-1 8 9-12h-7z" fill="currentColor"/>',
    vision: '<path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/>',
    adhd: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/>',
    keyboard: '<rect x="2" y="6" width="20" height="12" rx="2"/><path d="M6 10h0M10 10h0M14 10h0M18 10h0M6 14h12"/>',
    close: '<path d="M6 6l12 12M18 6L6 18"/>'
  };

  /* -------------------------------------------------- DOM build ---------- */
  var ui = {}; // references to interactive elements for re-render

  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }

  function buildToggleOpt(parent, key, icon, label, onClick) {
    var b = el('button', 'acc-opt', svg(icon) + '<span>' + label + '</span>');
    b.type = 'button';
    b.setAttribute('aria-pressed', 'false');
    b.addEventListener('click', onClick);
    parent.appendChild(b);
    return b;
  }

  function announce(msg) {
    if (ui.live) { ui.live.textContent = ''; ui.live.textContent = msg; }
  }
  function flag(label, isOn) { return label + ' – ' + (isOn ? t('on') : t('off')); }

  function resolvePosition(p) {
    var legacy = { right: 'bottom-right', left: 'bottom-left' };
    if (legacy[p]) p = legacy[p];
    var allowed = { 'bottom-right': 1, 'bottom-left': 1, 'middle-left': 1, 'middle-right': 1 };
    return allowed[p] ? p : 'middle-left';
  }

  function build() {
    var rootEl = el('div', 'acc-root acc-pos-' + resolvePosition(cfg.position));
    rootEl.setAttribute('dir', cfg.dir || 'ltr');
    if (cfg.lang) rootEl.setAttribute('lang', cfg.lang);
    rootEl.style.setProperty('--acc-accent', cfg.accent);

    // Toggle button
    var toggle = el('button', 'acc-toggle', svg(IC.wheelchair));
    toggle.type = 'button';
    toggle.setAttribute('aria-haspopup', 'dialog');
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', t('open'));
    toggle.title = t('open');
    ui.toggle = toggle;

    // Panel
    var panel = el('div', 'acc-panel');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-label', t('title'));
    panel.setAttribute('inert', '');
    ui.panel = panel;

    // Header
    var head = el('div', 'acc-head',
      '<span class="acc-head-icon">' + svg(IC.wheelchair) + '</span><h2>' + t('title') + '</h2>');
    var close = el('button', 'acc-close', svg(IC.close));
    close.type = 'button';
    close.setAttribute('aria-label', t('close'));
    close.title = t('close');
    close.addEventListener('click', closePanel);
    head.appendChild(close);
    panel.appendChild(head);

    // Body
    var body = el('div', 'acc-body');
    panel.appendChild(body);

    /* --- Profiles --- */
    body.appendChild(el('div', 'acc-group-title', t('g_profiles')));
    var gP = el('div', 'acc-grid');
    ui.profiles = {};
    var profiles = [
      ['seizure', IC.seizure, t('p_seizure')],
      ['vision', IC.vision, t('p_vision')],
      ['adhd', IC.adhd, t('p_adhd')],
      ['keyboard', IC.keyboard, t('p_keyboard')]
    ];
    profiles.forEach(function (p) {
      ui.profiles[p[0]] = buildToggleOpt(gP, p[0], p[1], p[2], function () { toggleProfile(p[0], p[2]); });
    });
    body.appendChild(gP);

    /* --- Content --- */
    body.appendChild(el('div', 'acc-group-title', t('g_content')));
    var gC = el('div', 'acc-grid');

    // Text size stepper
    var stepWrap = el('div', 'acc-step');
    var stepLabel = el('div', 'acc-step-label', svg(IC.text) + '<span>' + t('text_size') + '</span>');
    var dec = el('button', 'acc-step-btn', svg(IC.minus)); dec.type = 'button'; dec.setAttribute('aria-label', t('decrease'));
    var inc = el('button', 'acc-step-btn', svg(IC.plus)); inc.type = 'button'; inc.setAttribute('aria-label', t('increase'));
    var dots = el('div', 'acc-step-dots');
    for (var d = 0; d < 5; d++) dots.appendChild(el('span'));
    dec.addEventListener('click', function () { stepFont(-1); });
    inc.addEventListener('click', function () { stepFont(1); });
    stepWrap.appendChild(stepLabel);
    stepWrap.appendChild(dec); stepWrap.appendChild(dots); stepWrap.appendChild(inc);
    gC.appendChild(stepWrap);
    ui.fontDec = dec; ui.fontInc = inc; ui.fontDots = dots; ui.fontStep = stepWrap;

    // Spacing (cycles 0->1->2->0)
    ui.spacing = buildToggleOpt(gC, 'spacing', IC.spacing, t('spacing'), function () {
      state.spacing = (state.spacing + 1) % 3; clearProfile(); commit(flag(t('spacing'), state.spacing > 0));
    });
    ui.readableFont = buildToggleOpt(gC, 'readableFont', IC.font, t('readable_font'), function () {
      state.readableFont = !state.readableFont; clearProfile(); commit(flag(t('readable_font'), state.readableFont));
    });
    ui.highlightLinks = buildToggleOpt(gC, 'highlightLinks', IC.link, t('highlight_links'), function () {
      state.highlightLinks = !state.highlightLinks; clearProfile(); commit(flag(t('highlight_links'), state.highlightLinks));
    });
    ui.highlightTitles = buildToggleOpt(gC, 'highlightTitles', IC.title, t('highlight_titles'), function () {
      state.highlightTitles = !state.highlightTitles; clearProfile(); commit(flag(t('highlight_titles'), state.highlightTitles));
    });
    ui.alignLeft = buildToggleOpt(gC, 'alignLeft', IC.alignL, t('align_left'), function () { setAlign('left'); });
    ui.alignCenter = buildToggleOpt(gC, 'alignCenter', IC.alignC, t('align_center'), function () { setAlign('center'); });
    ui.alignRight = buildToggleOpt(gC, 'alignRight', IC.alignR, t('align_right'), function () { setAlign('right'); });
    body.appendChild(gC);

    /* --- Color --- */
    body.appendChild(el('div', 'acc-group-title', t('g_color')));
    var gK = el('div', 'acc-grid');
    ui.color = {};
    var colors = [
      ['contrast-dark', IC.contrastD, t('contrast_dark')],
      ['contrast-light', IC.contrastL, t('contrast_light')],
      ['invert', IC.invert, t('invert')],
      ['grayscale', IC.gray, t('grayscale')],
      ['sat-high', IC.satHi, t('sat_high')],
      ['sat-low', IC.satLo, t('sat_low')]
    ];
    colors.forEach(function (c) {
      ui.color[c[0]] = buildToggleOpt(gK, c[0], c[1], c[2], function () { setColor(c[0], c[2]); });
    });
    body.appendChild(gK);

    /* --- Navigation / orientation --- */
    body.appendChild(el('div', 'acc-group-title', t('g_nav')));
    var gN = el('div', 'acc-grid');
    ui.cursorBlack = buildToggleOpt(gN, 'cursorBlack', IC.cursorB, t('cursor_black'), function () { setCursor('black', t('cursor_black')); });
    ui.cursorWhite = buildToggleOpt(gN, 'cursorWhite', IC.cursorW, t('cursor_white'), function () { setCursor('white', t('cursor_white')); });
    ui.focus = buildToggleOpt(gN, 'focus', IC.focus, t('focus'), function () {
      state.focus = !state.focus; clearProfile(); commit(flag(t('focus'), state.focus));
    });
    ui.stopAnim = buildToggleOpt(gN, 'stopAnim', IC.motion, t('stop_anim'), function () {
      state.stopAnim = !state.stopAnim; clearProfile(); commit(flag(t('stop_anim'), state.stopAnim));
    });
    ui.guide = buildToggleOpt(gN, 'guide', IC.guide, t('guide'), function () {
      state.guide = !state.guide; clearProfile(); commit(flag(t('guide'), state.guide));
    });
    ui.mask = buildToggleOpt(gN, 'mask', IC.mask, t('mask'), function () {
      state.mask = !state.mask; clearProfile(); commit(flag(t('mask'), state.mask));
    });
    ui.hideImages = buildToggleOpt(gN, 'hideImages', IC.image, t('hide_images'), function () {
      state.hideImages = !state.hideImages; clearProfile(); commit(flag(t('hide_images'), state.hideImages));
    });
    ui.mute = buildToggleOpt(gN, 'mute', IC.mute, t('mute'), function () {
      state.mute = !state.mute; clearProfile(); commit(flag(t('mute'), state.mute));
    });
    body.appendChild(gN);

    // Footer
    var foot = el('div', 'acc-foot');
    var row = el('div', 'acc-foot-row');
    var statement = el('a', 'acc-btn acc-btn-statement', svg(IC.statement) + '<span>' + t('statement') + '</span>');
    statement.href = cfg.statement || '/pages/accessibility';
    var reset = el('button', 'acc-btn acc-btn-reset', svg(IC.reset) + '<span>' + t('reset') + '</span>');
    reset.type = 'button';
    reset.addEventListener('click', resetAll);
    row.appendChild(reset); row.appendChild(statement);
    foot.appendChild(row);
    panel.appendChild(foot);

    // Live region
    ui.live = el('div', 'acc-sr');
    ui.live.setAttribute('aria-live', 'polite');
    ui.live.setAttribute('role', 'status');

    rootEl.appendChild(toggle);
    rootEl.appendChild(panel);
    rootEl.appendChild(ui.live);

    toggle.addEventListener('click', function () { isOpen() ? closePanel() : openPanel(); });
    panel.addEventListener('keydown', onPanelKey);

    // Prepend so the toggle is among the first focusable elements on the page.
    document.body.insertBefore(rootEl, document.body.firstChild);
    ui.root = rootEl;
  }

  /* -------------------------------------------------- actions ------------ */
  function commit(msg) { clampState(); save(); applyState(); render(); if (msg) announce(msg); }

  function clampState() {
    if (state.fontSize < 0) state.fontSize = 0;
    if (state.fontSize > 5) state.fontSize = 5;
    if (state.spacing < 0) state.spacing = 0;
    if (state.spacing > 2) state.spacing = 2;
  }

  function stepFont(dir) {
    state.fontSize += dir; clearProfile(); clampState();
    commit(t('text_size') + ' ' + state.fontSize + '/5');
  }
  function setAlign(v) {
    state.align = (state.align === v) ? '' : v; clearProfile();
    commit(flag(t('align_' + v), state.align === v));
  }
  function setColor(v, label) {
    state.color = (state.color === v) ? '' : v; clearProfile();
    commit(flag(label, state.color === v));
  }
  function setCursor(v, label) {
    state.cursor = (state.cursor === v) ? '' : v; clearProfile();
    commit(flag(label, state.cursor === v));
  }

  var PROFILE_DEF = {
    seizure: { stopAnim: true, color: 'sat-low' },
    vision: { fontSize: 3, color: 'contrast-dark', readableFont: true, highlightLinks: true },
    adhd: { mask: true, color: 'sat-low', stopAnim: true },
    keyboard: { focus: true, highlightLinks: true }
  };
  function toggleProfile(name, label) {
    if (state.profile === name) {
      resetState(); commit(flag(label, false)); return;
    }
    resetState();
    var def = PROFILE_DEF[name];
    for (var k in def) state[k] = def[k];
    state.profile = name;
    commit(flag(label, true));
  }
  function clearProfile() { state.profile = ''; }

  function resetState() {
    for (var k in DEFAULTS) state[k] = DEFAULTS[k];
  }
  function resetAll() {
    resetState(); save(); applyState(); render(); announce(t('reset_done'));
  }

  /* -------------------------------------------------- render ------------- */
  function press(node, on) { if (node) node.setAttribute('aria-pressed', on ? 'true' : 'false'); }

  function render() {
    // font dots + buttons
    if (ui.fontDots) {
      var dots = ui.fontDots.children;
      for (var i = 0; i < dots.length; i++) dots[i].className = i < state.fontSize ? 'on' : '';
    }
    if (ui.fontDec) ui.fontDec.disabled = state.fontSize <= 0;
    if (ui.fontInc) ui.fontInc.disabled = state.fontSize >= 5;
    if (ui.fontStep) ui.fontStep.classList.toggle('is-active', state.fontSize > 0);

    press(ui.spacing, state.spacing > 0);
    if (ui.spacing) {
      var lvl = ui.spacing.querySelector('.acc-level');
      if (state.spacing > 0 && !lvl) { lvl = el('span', 'acc-level'); ui.spacing.appendChild(lvl); }
      if (lvl) lvl.textContent = state.spacing > 0 ? state.spacing + '/2' : '';
    }
    press(ui.readableFont, state.readableFont);
    press(ui.highlightLinks, state.highlightLinks);
    press(ui.highlightTitles, state.highlightTitles);
    press(ui.alignLeft, state.align === 'left');
    press(ui.alignCenter, state.align === 'center');
    press(ui.alignRight, state.align === 'right');

    for (var c in ui.color) press(ui.color[c], state.color === c);

    press(ui.cursorBlack, state.cursor === 'black');
    press(ui.cursorWhite, state.cursor === 'white');
    press(ui.focus, state.focus);
    press(ui.stopAnim, state.stopAnim);
    press(ui.guide, state.guide);
    press(ui.mask, state.mask);
    press(ui.hideImages, state.hideImages);
    press(ui.mute, state.mute);

    for (var p in ui.profiles) press(ui.profiles[p], state.profile === p);
  }

  /* -------------------------------------------------- open / close ------- */
  var inerted = [];
  function isOpen() { return ui.root && ui.root.classList.contains('is-open'); }

  // Modal isolation: hide the rest of the page from AT/focus while the dialog
  // is open, then restore it. The widget root is left interactive.
  function setBackgroundInert(on) {
    if (on) {
      inerted = [];
      var kids = document.body.children;
      for (var i = 0; i < kids.length; i++) {
        var c = kids[i];
        if (c === ui.root || c.hasAttribute('inert')) continue;
        c.setAttribute('inert', '');
        inerted.push(c);
      }
    } else {
      for (var j = 0; j < inerted.length; j++) inerted[j].removeAttribute('inert');
      inerted = [];
    }
  }

  function openPanel() {
    ui.root.classList.add('is-open');
    ui.toggle.setAttribute('aria-expanded', 'true');
    ui.panel.removeAttribute('inert');
    setBackgroundInert(true);
    var first = ui.panel.querySelector('.acc-close');
    if (first) first.focus();
    document.addEventListener('keydown', onDocKey, true);
  }
  function closePanel() {
    if (!isOpen()) return;
    ui.root.classList.remove('is-open');
    ui.toggle.setAttribute('aria-expanded', 'false');
    setBackgroundInert(false);
    ui.panel.setAttribute('inert', '');
    document.removeEventListener('keydown', onDocKey, true);
    ui.toggle.focus(); // return focus to the control that opened the dialog (APG)
  }
  function onDocKey(e) { if (e.key === 'Escape') { e.preventDefault(); closePanel(); } }

  function onPanelKey(e) {
    if (e.key !== 'Tab') return;
    var f = ui.panel.querySelectorAll('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])');
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* -------------------------------------------------- init --------------- */
  function init() {
    build();
    applyState();
    render();
    window.addEventListener('resize', positionAids, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
